import {
  Box,
  Card,
  CircularProgress,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';
import DefaultTooltip from '../Tooltips/DefaultTooltip';
import { InfoOutlined } from '@mui/icons-material';
import { formatDateTolocaleDateString } from '../../utils/formatDateTimes';
import EditIcon from '@mui/icons-material/Edit';
import { EditCustomerDialog } from './EditCustomerDialog';
import { PricingSheetRow } from '../../../../shared/types';
import { RentalHistorySourceType } from '../../../../shared/types/pricingSheets';
import { useState } from 'react';
import { isRentalHistoryRow } from '../../../../shared/helpers/isRentalHistoryRow';

const ANIMATION_DURATION = 200;

type RentalHistoryProps = {
  customerId: string;
  customerName: string;
  startRentalHistoryDate: Date;
  endRentalHistoryDate: Date;
  onCustomerChange: (
    rentalHistoryCustomerName: string,
    rentalHistoryCustomerId: string,
    rentalHistoryStartDate: Date,
    rentalHistoryEndDate: Date,
    rentalHistoryCompanyChainId: string,
    rentalHistorySourceType: RentalHistorySourceType,
    pricingSheetItems: PricingSheetRow[],
    rentalHistoryCompanyName: string,
  ) => void;
  dispatchPricingSheetRows: any;
  pricingSheetRows: PricingSheetRow[];
  companyChains?: any;
  companyChainId?: string;
  rentalHistorySourceType?: RentalHistorySourceType;
  rentalHistoryCompanyName?: string;
  isLoadingRentalHistory?: boolean;
};

export const RentalHistory = ({
  customerName,
  startRentalHistoryDate,
  endRentalHistoryDate,
  onCustomerChange,
  pricingSheetRows,
  companyChains,
  rentalHistorySourceType,
  rentalHistoryCompanyName,
  isLoadingRentalHistory = false,
}: RentalHistoryProps) => {
  const theme = useTheme();
  const [dialogOpen, setDialogOpen] = useState(false);

  return (
    <>
      <EditCustomerDialog
        open={dialogOpen}
        pricingSheetRows={pricingSheetRows}
        title={'Lataa vuokrahistoria'}
        companyChains={companyChains}
        onCancel={() => setDialogOpen(false)}
        onConfirm={(...props) => {
          onCustomerChange(...props);
          setDialogOpen(false);
        }}
      />
      <Card variant="outlined" sx={{ padding: 1, ml: 1 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-start',
            alignItems: 'center',
            [theme.breakpoints.down('md')]: {
              flexDirection: 'column',
            },
            gap: 2,
          }}
        >
          <Typography sx={{ ml: 1, fontWeight: 'bold' }}>
            Vuokraushistoria{' '}
          </Typography>

          <DefaultTooltip
            enterDelay={ANIMATION_DURATION}
            enterNextDelay={ANIMATION_DURATION}
            title={
              'Asiakkaan vuokrahistoria vuoden ajalta. Vuokrahistorian perusteella lasketaan painotettu hintakerroin hinnastolle.'
            }
            placement="top"
          >
            <InfoOutlined
              color={'info'}
              sx={{
                mt: 0.5,
                cursor: 'help',
                fontSize: 18,
              }}
            />
          </DefaultTooltip>
        </Box>

        {rentalHistorySourceType ===
        RentalHistorySourceType.Customer ? (
          <Typography sx={{ ml: 1 }}>
            {`Asiakas: ${customerName}`}
            <DefaultTooltip
              enterDelay={ANIMATION_DURATION}
              enterNextDelay={ANIMATION_DURATION}
              title={
                isLoadingRentalHistory
                  ? 'Asiakasta ei voi vaihtaa kun lataus on käynnissä'
                  : 'Vaihda asiakas'
              }
              placement={'top'}
            >
              <IconButton
                disabled={isLoadingRentalHistory}
                onClick={() => {
                  setDialogOpen(true);
                }}
                size={'small'}
                sx={{
                  ml: 0.5,
                  mb: 0.5,
                  fontSize: '1rem',
                  p: 0.5,
                  '.MuiSvgIcon-root': {
                    fontSize: 'inherit',
                  },
                }}
              >
                <EditIcon />
              </IconButton>
            </DefaultTooltip>
          </Typography>
        ) : (
          <Typography sx={{ ml: 1 }}>
            {`Yritysketju: ${rentalHistoryCompanyName}`}
            <DefaultTooltip
              enterDelay={ANIMATION_DURATION}
              enterNextDelay={ANIMATION_DURATION}
              title={'Vaihda yritysketju'}
              placement={'top'}
            >
              <IconButton
                onClick={() => {
                  setDialogOpen(true);
                }}
                size={'small'}
                sx={{
                  ml: 0.5,
                  mb: 0.5,
                  fontSize: '1rem',
                  p: 0.5,
                  '.MuiSvgIcon-root': {
                    fontSize: 'inherit',
                  },
                }}
              >
                <EditIcon />
              </IconButton>
            </DefaultTooltip>
          </Typography>
        )}

        <Typography sx={{ ml: 1 }}>
          {`Ajanjakso: ${startRentalHistoryDate ? formatDateTolocaleDateString(startRentalHistoryDate) : 'Ei asetettu'} - ${endRentalHistoryDate ? formatDateTolocaleDateString(endRentalHistoryDate) : 'Ei asetettu'}`}
        </Typography>
        <Typography sx={{ ml: 1 }}>
          Eri vuokratuotteiden määrä hinnastossa:{' '}
          {isLoadingRentalHistory ? (
            <CircularProgress
              color={'primary'}
              size={16}
              thickness={5}
            />
          ) : (
            pricingSheetRows.filter(
              (row) => row.preSelected && isRentalHistoryRow(row),
            ).length
          )}
        </Typography>
      </Card>
    </>
  );
};
